import { useContext, useEffect, useState } from 'react';

import { formatCEP } from '@brazilian-utils/brazilian-utils';
import { InformativeCard } from 'components/informative-card/informative-card';
import { AuthContext } from 'contexts/auth';
import { sendGTMEvent } from 'helpers/google-tag-manager';
import { bool, func, shape } from 'prop-types';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { customMutators } from 'utils/old-ui-commons/form';

import { Alert, Checkbox, Flex, Label, OptionalField, Text } from '@olist/united';

import FormInput from '../../../components/layout/form-input';
import PostalCodeSearchInput from '../../../components/layout/postal-code-search-input';
import SelectInput from '../../../components/layout/select-input';
import { AlertContext } from '../../../contexts/alert-context';
import Api from '../../../service';
import { informativeCardData } from './informative-card-data';
import { FieldPair, FieldWrapper } from './logistic-address-form.styles';
import { BR_STATE_OPTIONS, FIELD_NAMES, formValidate } from './utils';

const LogisticAddressForm = ({
  goToNextStep,
  hasHubCoverage,
  initialValues,
  isGtmLoaded,
  setHasHubCoverage,
  setShowFormAddress,
  setSubmitCallback,
  showFormAddress,
  updatePersonalData,
}) => {
  const { t } = useTranslation();

  const { setAlert } = useContext(AlertContext);
  const { userInfo } = useContext(AuthContext);

  const customInitialValues = initialValues;
  const [showCEPSearchError, setShowCEPSearchError] = useState(false);
  const [isAddressNumberOpen, toggleAddressNumber] = useState(false);

  const showAlertHasHubCoverage = hasHubCoverage && showFormAddress;
  const showAlertDoesNotHaveHubCoverage = !hasHubCoverage && showFormAddress;

  const verifyIfHasHubCoverage = async (postalCode) => {
    const address = await Api.getViaCEPAddressByCep(postalCode);
    setHasHubCoverage(address?.hasHubCoverage);
  };

  useEffect(() => {
    if (userInfo?.email && isGtmLoaded) {
      sendGTMEvent('page_view', { user_id: userInfo.email });
    }
  }, [userInfo?.email, isGtmLoaded]);

  useEffect(() => {
    const { postal_code: postalCode } = customInitialValues ?? {};
    if (!showFormAddress && !!postalCode) {
      verifyIfHasHubCoverage(postalCode);
      setShowFormAddress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customInitialValues, showFormAddress]);

  const onSubmitHandler = async (postValues) => {
    try {
      const responseValues = await Api.postLogisticAddress(postValues);
      if (responseValues) {
        updatePersonalData(responseValues);
        sendGTMEvent('etapa_cadastro', {
          etapa: 'endereco_logistico',
        });
        goToNextStep();
      }
    } catch (error) {
      setAlert({
        text: t`An error occurred and the information could not be sent. Try again.`,
        show: true,
        iconName: 'alert-triangle-outline',
      });
    }
  };

  const viaCepToFormValues = (viaCepAddress, mutator) => {
    const address = {
      street: viaCepAddress?.addressInfo?.logradouro ?? '',
      complement: viaCepAddress?.addressInfo?.complemento ?? '',
      neighbourhood: viaCepAddress?.addressInfo?.bairro ?? '',
      city: viaCepAddress?.addressInfo?.localidade ?? '',
      state: viaCepAddress?.addressInfo?.uf ?? '',
    };
    const addressTuple = Object.entries(address);
    addressTuple.forEach(([key, value]) => mutator(key, value));
  };

  const postalCode = customInitialValues?.postal_code ?? ``;
  if (!postalCode.includes(`-`) && postalCode.length > 5) {
    const postalCodeToArray = postalCode.split(``);
    postalCodeToArray.splice(5, 0, `-`);
    customInitialValues.postal_code = postalCodeToArray.join('');
  }

  const handleCEPStates = (formState, searchErrorState, hubCoverage, setInputValues = () => {}) => {
    setHasHubCoverage(hubCoverage);
    setShowFormAddress(formState);
    setShowCEPSearchError(searchErrorState);
    setInputValues();
  };

  const setNumberValue = (args, state, utils) => {
    const input = args[0]?.target?.value;
    const finalValue = input.replace(/[^0-9]+/g, '');

    utils.changeValue(state, FIELD_NAMES.number, () => finalValue);
  };

  const cleanInputNumber = (args, state, utils) => {
    const noNumberText = isAddressNumberOpen ? '' : 'S/N';
    utils.changeValue(state, FIELD_NAMES.number, () => noNumberText);
  };

  return (
    <Form
      mutators={{
        setValue: customMutators.setValue,
        setNumberValue,
        cleanInputNumber,
      }}
      onSubmit={onSubmitHandler}
      validate={formValidate}
      initialValues={customInitialValues}
      render={({ form, handleSubmit, hasValidationErrors, submitting }) => {
        setSubmitCallback(handleSubmit, !hasValidationErrors, submitting);
        return (
          <form onSubmit={handleSubmit}>
            <Field
              id={FIELD_NAMES.postalCode}
              name={FIELD_NAMES.postalCode}
              label={t`postal code`}
              placeholder="00000-000"
              parse={formatCEP}
              required
              onSearchError={() => handleCEPStates(true, true)}
              onSearchSuccess={(viaCepValues, hubCoverage) => {
                handleCEPStates(true, false, hubCoverage, () => {
                  return setTimeout(
                    () => viaCepToFormValues(viaCepValues, form.mutators.setValue),
                    100
                  );
                });
              }}
              component={PostalCodeSearchInput}
              data-testid="cepInput"
            />

            {showAlertHasHubCoverage && <InformativeCard {...informativeCardData[0]} />}
            {showAlertDoesNotHaveHubCoverage && <InformativeCard {...informativeCardData[1]} />}

            {showFormAddress && (
              <>
                <FieldPair>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.street}>{t`address`}</Label>
                    <Field
                      id={FIELD_NAMES.street}
                      name={FIELD_NAMES.street}
                      placeholder={t`type the address`}
                      required
                      component={FormInput}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.number}>{t`number`}</Label>
                    <Flex alignItems="flex-start">
                      <Flex flexDirection="column">
                        <Field
                          id={FIELD_NAMES.number}
                          component={FormInput}
                          disabled={isAddressNumberOpen}
                          maxLength={6}
                          name={FIELD_NAMES.number}
                          onChange={form.mutators.setNumberValue}
                          placeholder={t`type the number`}
                          required={!isAddressNumberOpen}
                        />
                      </Flex>
                      <Flex ml={2} mt={2} minWidth="125px">
                        <Field
                          data-testid="toggleAddressNumber"
                          component={Checkbox}
                          id={FIELD_NAMES.toggleAddressNumber}
                          name={FIELD_NAMES.toggleAddressNumber}
                          label={t`no number`}
                          onChange={(event) => {
                            const isChecked = event.target.checked;
                            form.mutators.cleanInputNumber();
                            form.mutators.setValue(FIELD_NAMES.toggleAddressNumber, isChecked);
                            toggleAddressNumber(isChecked);
                          }}
                          checked={isAddressNumberOpen}
                        />
                      </Flex>
                    </Flex>
                  </FieldWrapper>
                </FieldPair>
                <FieldPair>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.complement}>
                      {t`complement`} <OptionalField>{t`optional`}</OptionalField>
                    </Label>
                    <Field
                      id={FIELD_NAMES.complement}
                      name={FIELD_NAMES.complement}
                      placeholder={t`apt, block, house`}
                      component={FormInput}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.neighbourhood}>{t`neighbourhood`}</Label>
                    <Field
                      id={FIELD_NAMES.neighbourhood}
                      name={FIELD_NAMES.neighbourhood}
                      placeholder={t`type the neighbourhood`}
                      required
                      component={FormInput}
                    />
                  </FieldWrapper>
                </FieldPair>
                <FieldPair>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.city}>{t`city`}</Label>
                    <Field
                      id={FIELD_NAMES.city}
                      name={FIELD_NAMES.city}
                      placeholder={t`type the city`}
                      required
                      component={FormInput}
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <Label htmlFor={FIELD_NAMES.state}>{t`state`}</Label>
                    <Field
                      id={FIELD_NAMES.state}
                      name={FIELD_NAMES.state}
                      component={SelectInput}
                      options={BR_STATE_OPTIONS}
                      initialValue={initialValues?.state}
                      placeholder={t`select`}
                      noOptionsMsg={t`no state matches`}
                    />
                  </FieldWrapper>
                </FieldPair>
                {showCEPSearchError && (
                  <Alert
                    mt={4}
                    variation="warning"
                    messages={[
                      <Text key="1" color="foundation.shadeDarker">
                        {t`Zip code not found, fill in the fields to proceed`}
                      </Text>,
                    ]}
                  />
                )}
              </>
            )}
          </form>
        );
      }}
    />
  );
};

LogisticAddressForm.propTypes = {
  setSubmitCallback: func.isRequired,
  updatePersonalData: func.isRequired,
  goToNextStep: func.isRequired,
  initialValues: shape({}),
  isGtmLoaded: bool.isRequired,
  showFormAddress: bool.isRequired,
  setShowFormAddress: func.isRequired,
  setHasHubCoverage: func.isRequired,
  hasHubCoverage: bool.isRequired,
};

LogisticAddressForm.defaultProps = {
  initialValues: {},
};

export default LogisticAddressForm;
